import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MentionInput from './MentionInput';
import { parseBody, firstName, DAY_LABELS_SHORT } from '../utils/poa-helpers';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import { Appear } from '../components/ui/Appear';
import { FadeIn } from '../components/ui/FadeIn';

export interface POAItemModel {
  id: string;
  week_start: string;
  day: number;
  position: number;
  body: string;
  original_day: number;
  is_completable: boolean;
  completed_by: string | null;
  completed_at: string | null;
  // Live-derived from the creator's current capability flags. True when the
  // creator is manufacturing-only (no pilot, no manager) — used to badge the
  // item so pilots can see at a glance which entries came from the build team.
  created_by_is_manufacturing?: boolean;
}

interface Props {
  item: POAItemModel;
  isManager: boolean;
  /**
   * When true, the body uses line-clamp-2 by default and reveals on the
   * parent day-card's hover (group-hover:line-clamp-none). Compact day
   * columns set this; focus + agenda variants leave it false.
   */
  truncated?: boolean;
  onUpdate: (id: string, fields: { body?: string; is_completable?: boolean }) => Promise<void>;
  onComplete: (id: string, completed: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

/**
 * A single POA item row inside a day column.
 *
 * Composition:
 *   [grip] [checkbox?]  body (with mention chips)        [edit] [trash]
 *                       ↳ "Moved from Mon" subtitle if original_day != day
 *
 * The drag handle is its own element (not the entire row) so clicks on
 * the body / mention chips don't trigger drags.
 */
const POAItem: React.FC<Props> = ({ item, isManager, truncated = false, onUpdate, onComplete, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState(item.body);
  const [busy, setBusy] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { day: item.day, type: 'item' },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };

  const completed = !!item.completed_at;
  const moved = item.original_day !== item.day;

  const tokens = parseBody(item.body);

  const saveEdit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onUpdate(item.id, { body: draftBody });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    setDraftBody(item.body);
    setEditing(false);
  };

  const toggleCompletable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onUpdate(item.id, { is_completable: !item.is_completable });
    } finally {
      setBusy(false);
    }
  };

  const toggleComplete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onComplete(item.id, !completed);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    if (!window.confirm('Delete this item?')) return;
    setBusy(true);
    try {
      await onDelete(item.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-start gap-2 px-2 py-1.5 rounded-md
                  border border-transparent hover:border-gray-700/40
                  hover:bg-gray-700/15 transition-colors
                  ${isDragging ? 'ring-1 ring-primary/40 bg-gray-700/25' : ''}`}
    >
      {/* Drag handle (manager-only, surfaces on hover) */}
      {isManager && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          className="flex-shrink-0 mt-0.5 text-on-surface-disabled hover:text-on-surface-variant cursor-grab active:cursor-grabbing
                     opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
            <circle cx="2" cy="2" r="1.2" /><circle cx="8" cy="2" r="1.2" />
            <circle cx="2" cy="7" r="1.2" /><circle cx="8" cy="7" r="1.2" />
            <circle cx="2" cy="12" r="1.2" /><circle cx="8" cy="12" r="1.2" />
          </svg>
        </button>
      )}

      {/* Optional completion checkbox */}
      {item.is_completable && (
        <button
          type="button"
          onClick={toggleComplete}
          disabled={busy}
          aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
          className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-sm border transition-colors
                      ${completed
                        ? 'bg-emerald-500/80 border-emerald-400 text-white'
                        : 'bg-gray-900 border-outline-variant hover:border-emerald-400/60'}`}
        >
          <Appear show={completed}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden>
              <path d="M6.2 11.4 3 8.2l1.1-1.1 2.1 2.1L11.9 3.5 13 4.6z" />
            </svg>
          </Appear>
        </button>
      )}

      <FadeIn key={editing ? 'edit' : 'view'} className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-1.5">
            <MentionInput
              value={draftBody}
              onChange={setDraftBody}
              onSubmit={saveEdit}
              autoFocus
              rows={2}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveEdit}
                disabled={busy || !draftBody.trim()}
                className={buttonClass('filled', 'sm')}
              >
                Save
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className={buttonClass('quiet', 'sm')}
              >
                Cancel
              </button>
              <label className="ml-auto flex items-center gap-1 text-[10px] text-on-surface-variant select-none">
                <input
                  type="checkbox"
                  checked={item.is_completable}
                  onChange={toggleCompletable}
                  className="accent-primary"
                />
                Trackable
              </label>
            </div>
          </div>
        ) : (
          <>
            <div className={`text-xs leading-relaxed break-words ${
              completed ? 'line-through text-on-surface-variant' : 'text-on-surface'
            } ${truncated ? 'line-clamp-2 group-hover:line-clamp-none' : ''}`}>
              {tokens.map((tok, i) =>
                tok.type === 'mention' ? (
                  <span
                    key={i}
                    title={tok.fullName}
                    className="inline-flex items-baseline px-1.5 py-0.5 mx-0.5 rounded
                               text-[10px] font-medium bg-primary/10 text-primary-ink
                               ring-1 ring-inset ring-primary/20"
                  >
                    @{firstName(tok.fullName)}
                  </span>
                ) : (
                  <span key={i}>{tok.value}</span>
                )
              )}
            </div>
            <Reveal show={!!(moved || item.created_by_is_manufacturing)}>
              <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-on-surface-variant italic">
                {moved && <span>Moved from {DAY_LABELS_SHORT[item.original_day]}</span>}
                {item.created_by_is_manufacturing && (
                  <span
                    title="Added by Manufacturing"
                    className="not-italic inline-flex items-center px-1 py-px rounded
                               text-[9px] font-semibold tracking-wide
                               bg-amber-500/10 text-amber-300 ring-1 ring-inset ring-amber-500/30"
                  >
                    MFG
                  </span>
                )}
              </div>
            </Reveal>
          </>
        )}
      </FadeIn>

      {isManager && (
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
      <Appear show={!editing} as="div" className="flex items-center gap-1">
          <Tooltip label="Edit this item">
          <IconButton
            onClick={() => { setDraftBody(item.body); setEditing(true); }}
            label="Edit"
            size="sm"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <path d="M11 1.5 14.5 5 5 14.5H1.5V11z" strokeLinejoin="round" />
            </svg>
          </IconButton>
          </Tooltip>
          <Tooltip label="Delete this item">
          <IconButton
            onClick={handleDelete}
            label="Delete"
            size="sm"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <path d="M3 4h10M5.5 4V2.5h5V4M4.5 4l1 10h5l1-10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>
          </Tooltip>
      </Appear>
      </div>
      )}
    </div>
  );
};

export default POAItem;
